import React, { useEffect, useRef, useState } from "react";
import useSWR from "swr";

import LeaderboardTableRow from "./LeaderboardTableRow";
import LoadMorePagination from "../../InventoryManagement/components/Common/LoadMorePagination";
import EmployeeDeepDiveModal from "./EmployeeDeepDiveModal";
import { KPIDashboardService } from "../service/kpiDashboardService";

import "../styles/KPILeaderboardTable.scss";
import EmptyCard from "../../InventoryManagement/components/Common/EmptyCard";
import ErrorCard from "../../InventoryManagement/components/Common/ErrorCard";

const KPILeaderboardTable = ({
  hierarchy,
  metric = null,
  query = "",
  geoFilters = {},
  setLastUpdated,
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [viewEmployeeDeepDive, setViewEmployeeDeepDive] = useState(false);
  const [focusedUserId, setFocusedUserId] = useState(null);
  const [employeeDataType, setEmployeeDataType] = useState("kpi-parameters");
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({
    data: [],
    count: 0,
  });
  const [pageSize, setPageSize] = useState(0);

  const criteriaSignature = JSON.stringify({
    hierarchy,
    metric,
    query,
    sortField,
    sortDir,
    geoFilters,
  });

  const previousCriteriaSignatureRef = useRef(criteriaSignature);
  const loadedPagesRef = useRef(new Set());

  useEffect(() => {
    const hasCriteriaChanged =
      previousCriteriaSignatureRef.current !== criteriaSignature;

    if (!hasCriteriaChanged) return;

    previousCriteriaSignatureRef.current = criteriaSignature;
    loadedPagesRef.current.clear();
    setPageSize(0);
    setData({
      data: [],
      count: 0,
    });

    if (page !== 0) {
      setPage(0);
    }
  }, [criteriaSignature, page]);

  const {
    data: response,
    error,
    isValidating,
  } = useSWR(
    ["kpi-breakdown", criteriaSignature, page],
    () =>
      KPIDashboardService.kpiBreakdownFetcher({
        page,
        hierarchy,
        metric,
        query,
        geoFilters,
        sort:
          sortField && sortDir
            ? { sortBy: sortField, sortOrder: sortDir }
            : null,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (!response) return;

    const rows = response?.data || [];

    setData((prev) => {
      if (page === 0) {
        loadedPagesRef.current = new Set([0]);
        return {
          data: rows,
          count: response?.count || 0,
        };
      }

      if (loadedPagesRef.current.has(page)) {
        return {
          ...prev,
          count: response?.count || 0,
        };
      }

      loadedPagesRef.current.add(page);
      return {
        data: [...prev.data, ...rows],
        count: response?.count || 0,
      };
    });

    if (page === 0) {
      setPageSize(rows.length);
    }

    if (setLastUpdated && response?.as_of_date) {
      setLastUpdated(response.as_of_date);
    }
  }, [page, response, setLastUpdated]);

  const handleSetPage = (newPage) => {
    setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortField !== field || !sortDir) {
      setSortField(field);
      setSortDir("asc");
      return;
    }

    if (sortDir === "asc") {
      setSortDir("desc");
      return;
    }

    setSortField(null);
    setSortDir(null);
  };

  const handleEmployeeView = (userId, viewType) => {
    setViewEmployeeDeepDive(true);
    setFocusedUserId(userId);
    setEmployeeDataType(viewType);
  };

  const loading = page === 0 && isValidating && data.data.length === 0;
  const isLoadingMore = page > 0 && isValidating;

  const totalPages =
    pageSize > 0 ? Math.ceil((data?.count || 0) / pageSize) : 0;
  const showAdditionalColumns = metric && metric !== "overall";
  if (loading) {
    return (
      <div className="kpi-table-card">
        <div className="scroll-container">
          <div className="table-content">
            <LeaderboardTableRow
              variant="header"
              showAdditionalColumns={showAdditionalColumns}
            />
            {[...Array(10)].map((_, i) => (
              <LeaderboardTableRow
                key={i}
                variant="loading"
                showAdditionalColumns={showAdditionalColumns}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-table-card">
        <ErrorCard
          title="Error Loading Data"
          message="There was an error while fetching the leaderboard data. Please try again later."
        />
      </div>
    );
  }

  return (
    <div className="kpi-table-card">
      <div className="scroll-container">
        <div className="table-content">
          {data.data.length === 0 ? (
            <EmptyCard
              title="No Data Available"
              message="No Leaderboard data matches the selected criteria. Please adjust your filters and try again."
            />
          ) : (
            <>
              <LeaderboardTableRow
                variant="header"
                sortField={sortField}
                sortDir={sortDir}
                handleSort={handleSort}
                hierarchy={hierarchy}
                showAdditionalColumns={showAdditionalColumns}
              />

              {data?.data.map((row, i) => (
                <LeaderboardTableRow
                  key={i}
                  row={row}
                  rowIndex={i + 1}
                  focusedRowIndex={focusedRowIndex}
                  setFocusedRowIndex={setFocusedRowIndex}
                  onEmployeeMenuClick={handleEmployeeView}
                  isEmployeeDeepDiveOpen={viewEmployeeDeepDive}
                  showAdditionalColumns={showAdditionalColumns}
                />
              ))}
            </>
          )}
        </div>
        <LoadMorePagination
          page={page}
          setPage={handleSetPage}
          totalPages={totalPages}
          isLoadingMore={isLoadingMore}
        />
        {viewEmployeeDeepDive && (
          <EmployeeDeepDiveModal
            userData={{
              userId: focusedUserId,
              username: data?.data[focusedRowIndex - 1]?.user_name || "",
            }}
            employeeDataType={employeeDataType}
            setViewEmployeeDeepDive={setViewEmployeeDeepDive}
            metric={metric}
          />
        )}
      </div>
    </div>
  );
};

export default KPILeaderboardTable;
